import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Settings, Shield, Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';

import { supportTicketService } from '../services/supportTicketService';

export default function Header() {
  const navigate = useNavigate();
  const { profile, loading, impersonationContext, refreshProfile, signOut } = useAuth();
  const { branding, loading: brandingLoading } = useBranding();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showImpersonationBanner, setShowImpersonationBanner] = useState(false);
  const [endingImpersonation, setEndingImpersonation] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [notificationSubscription, setNotificationSubscription] = useState(null);

  useEffect(() => {
    // Check for impersonation context
    if (impersonationContext) {
      setShowImpersonationBanner(true);
    } else {
      setShowImpersonationBanner(false);
    }
  }, [impersonationContext]);

  // Load unread message count on mount and subscribe to new messages
  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const count = await supportTicketService?.getUnreadMessageCount();
        setUnreadMessageCount(count);
      } catch (error) {
        console.error('Error loading unread message count:', error);
      }
    };

    // Only show notifications for non-admin users
    if (profile && profile?.role !== 'admin') {
      loadUnreadCount();
      
      // Subscribe to new admin messages
      const subscription = supportTicketService?.subscribeToNewAdminMessages(() => {
        console.log('[Header] New admin message notification received');
        loadUnreadCount();
      });
      
      setNotificationSubscription(subscription);
      
      return () => {
        if (subscription) {
          supportTicketService?.unsubscribeFromTicketMessages(subscription);
        }
      };
    }
  }, [profile]);

  // Add handler to refresh notification count when navigating away from support tickets
  useEffect(() => {
    const handleRouteChange = () => {
      // Refresh count when leaving support tickets page
      if (profile && profile?.role !== 'admin') {
        supportTicketService?.getUnreadMessageCount()?.then(count => {
          setUnreadMessageCount(count);
        })?.catch(error => {
          console.error('Error refreshing notification count:', error);
        });
      }
    };

    // Listen for route changes
    window?.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window?.removeEventListener('popstate', handleRouteChange);
    };
  }, [profile]);

  const handleSignOut = async () => {
    try {
      // Call the updated signOut function which handles impersonation
      await signOut();
      
      // If there was impersonation, signOut will clear it and we navigate to admin dashboard
      if (impersonationContext) {
        navigate('/admin-dashboard');
        // Force reload to ensure clean state
        window.location?.reload();
        return;
      }
      
      // Normal sign out - navigate to auth
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleEndImpersonation = async () => {
    try {
      setEndingImpersonation(true);
      
      // Call signOut which now handles impersonation cleanup
      await signOut();
      
      // Navigate back to admin dashboard
      navigate('/admin-dashboard');
      
      // Force reload to clear impersonation state
      window.location?.reload();
    } catch (error) {
      console.error('Error ending impersonation:', error);
      alert('Failed to end impersonation: ' + error?.message);
    } finally {
      setEndingImpersonation(false);
    }
  };

  return (
    <>
      {/* Impersonation Warning Banner */}
      {showImpersonationBanner && impersonationContext && (
        <div className="bg-yellow-500 text-black px-4 py-2 flex items-center justify-between fixed top-0 left-64 right-0 z-50">
          <div className="flex items-center gap-2">
            <span className="font-semibold">⚠️ ADMIN IMPERSONATION ACTIVE</span>
            <span>
              Viewing as: <strong>{profile?.fullName}</strong> ({profile?.email})
            </span>
            <span className="text-xs opacity-75">
              Admin: {impersonationContext?.adminEmail}
            </span>
          </div>
          <button
            onClick={handleEndImpersonation}
            disabled={endingImpersonation}
            className="px-4 py-1 bg-black text-yellow-500 rounded hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {endingImpersonation ? 'Ending...' : 'End Impersonation'}
          </button>
        </div>
      )}
      <header className={`bg-white shadow-sm border-b border-gray-200 sticky z-40 ml-64 ${showImpersonationBanner ? 'top-10' : 'top-0'}`}>
        <div className="pr-6 pl-4 py-0">
          <div className="flex items-center justify-between h-16">
            {/* Logo/Title Section - MATCH ADMIN SIDEBAR PATTERN */}
            {!brandingLoading && (
              <div className="flex items-center gap-3">
                {branding?.logoUrl ? (
                  <img
                    src={branding?.logoUrl}
                    alt={branding?.brandName || 'Logo'}
                    className="h-10 w-auto object-contain"
                  />
                ) : (branding?.brandName || branding?.slogan) ? (
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                      <User className="w-5 h-5" />
                    </div>
                    <div>
                      {branding?.brandName && (
                        <h1 className="text-xl font-bold text-gray-900">{branding?.brandName}</h1>
                      )}
                      {branding?.slogan && (
                        <p className="text-xs text-gray-500">{branding?.slogan}</p>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* User Profile Section */}
            <div className="flex items-center gap-4">
              {/* Support Notification Bell - Only for non-admin users */}
              {profile && profile?.role !== 'admin' && (
                <button
                  onClick={() => navigate('/support-tickets')}
                  className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Support Messages"
                >
                  <Bell className="w-5 h-5 text-gray-600" />
                  {unreadMessageCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                    </span>
                  )}
                </button>
              )}

              {profile && (
                <div className="relative">
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {impersonationContext && (
                        <Shield className="w-4 h-4 text-yellow-500" />
                      )}
                      <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                        {profile?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    </div>
                    <div className="text-left hidden md:block">
                      <p className="text-sm font-semibold text-gray-900">
                        {profile?.fullName}
                      </p>
                      <p className="text-xs text-gray-500">{profile?.email}</p>
                    </div>
                  </button>

                  {/* Profile Dropdown */}
                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                      {impersonationContext && (
                        <>
                          <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-100">
                            <p className="text-xs font-semibold text-yellow-800 flex items-center gap-1">
                              <Shield className="w-3 h-3" />
                              Impersonation Mode
                            </p>
                            <p className="text-xs text-yellow-600 mt-1">
                              Viewing as: {impersonationContext?.impersonatedUserEmail}
                            </p>
                          </div>
                        </>
                      )}
                      
                      <button
                        onClick={() => {
                          navigate('/business-settings-configuration');
                          setShowProfileMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Settings className="w-4 h-4" />
                        Account Settings
                      </button>

                      {profile?.role === 'admin' && !impersonationContext && (
                        <button
                          onClick={() => {
                            navigate('/admin-dashboard');
                            setShowProfileMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Shield className="w-4 h-4" />
                          Admin Dashboard
                        </button>
                      )}

                      <hr className="my-2" />

                      <button
                        onClick={handleSignOut}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        {impersonationContext ? 'Exit Impersonation' : 'Sign Out'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}